import moment from "moment";
import { FeedbackValue, GeneralFeedbackTypeEnum } from "../types";

/**
 * Format a date for feedback display
 * @param date - Date to format (Date object or string)
 * @returns Formatted date string (e.g., "Dec 30, 2025 at 3:45 PM")
 */
export const formatFeedbackDate = (date: Date | string): string => {
  return moment(date).format("MMM D, YYYY [at] h:mm A");
};

/**
 * Get human-readable label for feedback usefulness rating
 * @param feedbackValue - Feedback value enum or string
 * @returns Usefulness label or null if no rating
 */
export const getUsefulnessLabel = (
  feedbackValue?: FeedbackValue | string,
): string | null => {
  if (!feedbackValue) return null;

  const valueStr =
    typeof feedbackValue === "string" ? feedbackValue : String(feedbackValue);

  // Special case: moderately_useful -> "Useful" (not "Moderately Useful")
  if (
    valueStr === FeedbackValue.MODERATELY_USEFUL ||
    valueStr === "moderately_useful"
  ) {
    return "Useful";
  }

  // Special case: not_at_all_useful -> "Not that useful"
  if (
    valueStr === FeedbackValue.NOT_AT_ALL_USEFUL ||
    valueStr === "not_at_all_useful"
  ) {
    return "Not that useful";
  }

  // Default: Very Useful
  if (valueStr === FeedbackValue.VERY_USEFUL || valueStr === "very_useful") {
    return "Very Useful";
  }

  return null;
};

/**
 * Get human-readable label for feedback type
 * @param feedbackType - Feedback type enum or string
 * @returns Feedback type label
 */
export const getFeedbackTypeLabel = (
  feedbackType?: GeneralFeedbackTypeEnum | string,
): string => {
  if (!feedbackType) return "Feedback";

  const typeStr =
    typeof feedbackType === "string" ? feedbackType : String(feedbackType);

  switch (typeStr) {
    case GeneralFeedbackTypeEnum.PAGE_FEEDBACK:
    case "page_feedback":
      return "Page Feedback";
    case GeneralFeedbackTypeEnum.GENERAL_FEEDBACK:
    case "general_feedback":
      return "General Feedback";
    case GeneralFeedbackTypeEnum.BUG_REPORT:
    case "bug_report":
      return "Bug Report";
    case GeneralFeedbackTypeEnum.FEATURE_REQUEST:
    case "feature_request":
      return "Feature Request";
    case GeneralFeedbackTypeEnum.LESSON_FEEDBACK:
    case "lesson_feedback":
      return "Lesson Feedback";
    default:
      return "Feedback";
  }
};
